import { useEffect, Fragment, useMemo } from 'react'
import { extractRootDomain } from '../../../../utils/Utils'
import { useTranslation } from 'react-i18next'
import { SerpEvolutionChart } from './SerpEvolutionChart'
import { Box, Typography, List, ListItem, Link, Paper, Tooltip, Divider } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import RemoveIcon from '@mui/icons-material/Remove'

const PositionIndicator = ({ currentRank, previousRank }) => {
  const { t } = useTranslation()

  const iconSx = { verticalAlign: 'middle', fontSize: '1.1rem' }
  if (previousRank === undefined) return <Box sx={{ width: 20 }} />
  if (currentRank < previousRank) return <Tooltip title={`${t('trackingPositionImprove')} ${previousRank}`}><ArrowUpwardIcon sx={{ ...iconSx, color: 'success.main' }} /></Tooltip>
  if (currentRank > previousRank) return <Tooltip title={`${t('trackingPositionWorse')} ${previousRank}`}><ArrowDownwardIcon sx={{ ...iconSx, color: 'error.main' }} /></Tooltip>
  return <RemoveIcon sx={{ ...iconSx, color: 'text.disabled' }} />
}

const SerpsView = ({ row, project, handleGetSerps }) => {
  const { t } = useTranslation()
  useEffect(() => {
    if (row.serps === undefined && row.keyword?.id) {
      handleGetSerps(row)
    }
  }, []);

  const historyWithSerps = row.keyword?.history || []
  const sortedHistory = [...historyWithSerps].sort((a, b) => new Date(b.date) - new Date(a.date))
  const lastHistoryEntry = sortedHistory[0] || null
  const previousHistoryEntry = sortedHistory[1] || null
  const currentSerps = lastHistoryEntry?.serps || []
  const previousRanks = new Map(
    previousHistoryEntry?.serps?.map((serp) => {
      const key = row.keyword.se === 'youtube' ? serp.channelUrl : serp.url
      return [key, serp.rank_group]
    }) || []
  )

  const sortedSerps = [...currentSerps].sort((a, b) => a.rank_group - b.rank_group)

  if (sortedSerps.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>{t('no_data')}</Typography>
      </Box>
    )
  }
  const userDomain = lastHistoryEntry?.domain || ''

  return (
    <Box sx={{ p: 1, backgroundColor: 'grey.50' }}>
      <Typography gutterBottom sx={{ p: 1 }}>{t('searchResults')}</Typography>
      <Paper style={{ maxHeight: 400, overflow: 'auto' }}>
        <List sx={{ p: 0 }}>
          {sortedSerps.map((serp, index) => {
            const userRootDomain = extractRootDomain(userDomain)
            const urlKey = row.keyword.se === 'youtube' ? serp.channelUrl : serp.url
            const isUserDomain = row.keyword.se === 'youtube' ? project.url === serp.channelUrl : userRootDomain === extractRootDomain(serp.url)
            const previousRank = previousRanks.get(urlKey)
            const evolutionData = row.keyword.history.map(h => {
              const serpForThisDay = h.serps.find(s => s.url === serp.url)
              return {
                date: h.date,
                rankGroup: serpForThisDay ? serpForThisDay.rank_group : null
              }
            }).reverse()

            const backgroundColor = isUserDomain ? 'rgba(25, 118, 210, 0.12)' : 'transparent'

            return (
              <Fragment key={serp.id}>
                <ListItem sx={{ m: 0, backgroundColor, p: 0, '&:hover': { backgroundColor: isUserDomain ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.08)' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', py: 0.75, px: 2, gap: 1.5 }}>
                    <Box sx={{ width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <PositionIndicator currentRank={serp.rank_group} previousRank={previousRank} />
                    </Box>
                    <Typography sx={{ fontWeight: 'bold' }}>{`${serp.rank_group}.`}</Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flexShrink: 1, mr: 1 }}>
                      <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {urlKey}
                      </Typography>
                      <Link href={serp.url} target='_blank' rel='noopener noreferrer' sx={{ display: 'flex', alignItems: 'center', ml: 0.75 }}>
                        <OpenInNewIcon sx={{ fontSize: '1rem', color: 'text.secondary', '&:hover': { color: 'primary.main' } }} />
                      </Link>
                    </Box>

                    {/* space in the midle */}
                    <Box sx={{ flexGrow: 1 }} />

                    <Box sx={{ width: 150, height: 24, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                      <SerpEvolutionChart history={evolutionData} />
                    </Box>
                  </Box>
                </ListItem>
                {index < sortedSerps.length - 1 && <Divider component='li' sx={{ m: 0 }} />}
              </Fragment>
            )
          })}
        </List>
      </Paper>
    </Box>
  )
}

export default SerpsView




